import { Injectable } from '@angular/core';

import { combineLatest, filter, map, of, switchMap, tap } from 'rxjs';

import {
  CamBaseService,
  GraphEndpoint,
  GraphReponsePaged,
  HandleComplexRequest,
  HandleSimpleRequest,
  createQuery,
  graphAggregatorEndpoint,
} from '@camelot/server';
import {
  Project,
  ProjectCreationPayloadInput,
  ProjectModifierPayloadInput,
  addressProps,
  contactProps,
  projectAddressProps,
  projectProps,
} from '@camelot/services';
import { isNonNullable, keepUniqueObjectByProperty } from '@camelot/utils';

import { companyProps } from '../../organization/services/dto/company';
import { departmentProps } from '../../organization/services/dto/department';
import { ProjectExtended, nestedProjectProps, projectExtendedProps } from './dto/project';
import {
  CREATE_PROJECT,
  GET_LIGHT_PROJECTS,
  GET_MY_PROJECTS,
  GET_PROJECTS,
  GET_PROJECT_BY_ID,
  GET_PROJECT_DOCUMENT_IDS,
  GET_PROJECT_TENANT_ROUTE,
  GET_SEARCH_PROJECTS,
  LINK_PROJECT_DOCUMENTS,
  UPDATE_PROJECT,
} from './queries';

const graphEndpoint: GraphEndpoint = {
  clientName: 'projectService',
  endpoint: 'project',
};

/** Références fédérées d'un projet, résolues en une seule query via l'agrégateur. */
const nestedAggregatorProps: Record<string, string> = {
  contact: `${projectProps.get('contact')} {
    ${contactProps.get('id')}
    ${contactProps.get('firstName')}
    ${contactProps.get('lastName')}
    ${contactProps.get('mail')}
    ${contactProps.get('phoneNumber')}
  }`,
  department: `department {
    ${departmentProps.get('id')}
    ${departmentProps.get('name')}
  }`,
  address: `address {
    ${addressProps.get('id')}
    ${addressProps.get('city')}
    ${addressProps.get('street')}
    ${addressProps.get('complement')}
    ${addressProps.get('postalCode')}
    ${addressProps.get('countryCode')}
  }`,
  company: `company {
    ${companyProps.get('id')}
    ${companyProps.get('name')}
  }`,
  parentProject: `parentProject {
    ${projectProps.get('id')}
    ${projectProps.get('name')}
  }`,
};

@Injectable({
  providedIn: 'root',
})
export class CamProjectsService extends CamBaseService {
  protected _graphEndpoint = graphEndpoint;
  public projects = new HandleSimpleRequest<Project[]>();
  public project = new HandleComplexRequest<Project>();

  public tenantRoute = new HandleSimpleRequest<string>();

  public projectByContact = new HandleComplexRequest<Project[]>();

  public projectDocumentIds = new HandleComplexRequest<string[]>();
  public projectDetail = new HandleComplexRequest<ProjectExtended>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public getProjectsLightInfo$(ids: string[]) {
    return this._graphService
      .fetchPagedQueryList<Project>(GET_LIGHT_PROJECTS(ids), 'projects', graphEndpoint.clientName)
      .pipe(map(data => data.items ?? []));
  }

  public searchProjects$(filter: string) {
    if (!filter || filter.length < 3) {
      return of<Project[]>([]);
    }
    return this._graphService
      .fetchPagedQueryList<Project>(GET_SEARCH_PROJECTS(filter), 'searchProjects', graphEndpoint.clientName)
      .pipe(map(data => data.items ?? []));
  }

  public fetchSearchProjects$(search?: string | null, selectedIds?: string[]) {
    const ids = (selectedIds ?? []).filter(isNonNullable);
    const search$ = this.searchProjects$(search ?? '');
    if (!ids.length) {
      return search$;
    }

    return combineLatest([this.getProjectsLightInfo$(ids), search$]).pipe(
      map(([selected, results]) => keepUniqueObjectByProperty([...selected, ...results], project => project.id))
    );
  }

  public fetchProjectsByContact$(contactId: string) {
    return this.projectByContact.fetch(
      contactId,
      this._graphService
        .fetchPagedQueryList<Project>(
          GET_PROJECTS(
            `where: { contactId: { eq: "${contactId}" } }`,
            `
              ${projectProps.get('id')}
              ${projectProps.get('name')}
              ${projectProps.get('status')}
              ${projectProps.get('departmentId')}
              ${projectProps.get('addressId')}
              ${projectProps.get('projectAddress')}
               {
                  ${projectAddressProps.get('city')}
                  ${projectAddressProps.get('street')}
                  ${projectAddressProps.get('postCode')}
                  ${projectAddressProps.get('country')}
                }
            `
          ),
          'projects',
          graphEndpoint.clientName
        )
        .pipe(map(data => data.items ?? []))
    );
  }

  public fetchProjects$() {
    return this.projects.fetch(
      this._graphService
        .fetchPagedQueryList<Project>(GET_MY_PROJECTS(), 'projects', graphEndpoint.clientName)
        .pipe(map(data => data.items))
    );
  }

  public fetchProject$(id: string) {
    return this.project.fetch(
      id,
      this._graphService.fetchQuery<Project>(GET_PROJECT_BY_ID(id), 'projectById', graphEndpoint.clientName)
    );
  }

  public fetchDynamicProps$(id: string, props: Array<keyof ProjectExtended>) {
    const graphqlProps = props
      .map(
        prop =>
          nestedAggregatorProps[prop.toString()] ??
          nestedProjectProps[prop.toString()] ??
          projectExtendedProps.get(prop)
      )
      .join(' ');

    return this._graphService
      .fetchQueryBuilder<GraphReponsePaged<ProjectExtended>>(
        createQuery<ProjectExtended>('projects', {
          where: { id: { eq: id } },
          prefixType: 'Project',
          props: `
            items {
              ${graphqlProps}
            }
            totalCount
          `,
        }),
        graphAggregatorEndpoint.clientName
      )
      .pipe(
        filter(isNonNullable),
        map(data => (data.items && data.items.length > 0 ? data.items[0] : null))
      );
  }

  public fetchTenantRoute$(projectId: string) {
    return this.tenantRoute.fetch(
      this._graphService
        .fetchQuery<string>(GET_PROJECT_TENANT_ROUTE(projectId), 'projectTenantRoute', graphEndpoint.clientName)
        .pipe(filter(isNonNullable))
    );
  }

  public fetchProjectDocumentIds$(projectId: string) {
    return this.projectDocumentIds.fetch(
      projectId,
      this._graphService.fetchQuery<string[]>(
        GET_PROJECT_DOCUMENT_IDS(projectId),
        'projectDocumentIds',
        graphEndpoint.clientName
      )
    );
  }

  public fetchProjectDetail$(id: string) {
    return this.projectDetail.fetch(
      id,
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<ProjectExtended>>(
          createQuery<ProjectExtended>('projects', {
            where: { id: { eq: id } },
            prefixType: 'Project',
            props: `
              items {
                ${projectProps.get('id')}
                ${projectProps.get('name')}
                ${projectProps.get('status')}
                ${projectProps.get('contactId')}
                ${projectProps.get('addressId')}
                ${projectProps.get('documentIds')}
                ${projectProps.get('departmentId')}
                ${projectProps.get('projectPictureUrl')}
                ${projectProps.get('pictureDocumentId')}
                ${projectProps.get('internalReference')}
                ${projectProps.get('clientReference')}
                ${projectProps.get('description')}
                ${projectProps.get('companyId')}
                ${projectProps.get('parentProjectId')}
                ${nestedProjectProps[projectProps.get('projectAddress')?.toString() ?? '']}
                ${projectProps.get('contact')} {
                  ${contactProps.get('id')}
                  ${contactProps.get('firstName')}
                  ${contactProps.get('lastName')}
                  ${contactProps.get('mail')}
                  ${contactProps.get('phoneNumber')}
                }
                department {
                  ${departmentProps.get('id')}
                  ${departmentProps.get('name')}
                }
                address {
                  ${addressProps.get('id')}
                  ${addressProps.get('city')}
                  ${addressProps.get('street')}
                  ${addressProps.get('complement')}
                  ${addressProps.get('postalCode')}
                  ${addressProps.get('countryCode')}
                }
                company {
                  ${companyProps.get('id')}
                  ${companyProps.get('name')}
                }
                parentProject {
                  ${projectProps.get('id')}
                  ${projectProps.get('name')}
                }
              },
              totalCount
            `,
          }),
          graphAggregatorEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => (data.items && data.items.length > 0 ? data.items[0] : null)),
          filter(isNonNullable)
        )
    );
  }

  public createProject$(payload: ProjectCreationPayloadInput) {
    return this._graphService
      .mutateBuilder<Project>(CREATE_PROJECT(payload), graphEndpoint.clientName)
      .pipe(filter(isNonNullable));
  }

  public updateProject$(payload: Partial<ProjectModifierPayloadInput> & { id: string }) {
    return this._graphService
      .mutateBuilder<Project>(UPDATE_PROJECT(payload), graphEndpoint.clientName, ['projects'])
      .pipe(
        filter(isNonNullable),
        tap(updated => {
          const existing = this.projectDetail.get(updated.id);
          if (existing) {
            this.projectDetail.update(updated.id, { ...existing, ...updated }, true);
          }
        }),
        switchMap(updated => this.fetchProjectDetail$(updated.id).pipe(map(() => updated)))
      );
  }

  public linkProjectDocuments$(projectId: string, documentIds: string[]) {
    return this._graphService
      .mutateBuilder<string[]>(LINK_PROJECT_DOCUMENTS(projectId, documentIds), graphEndpoint.clientName, [
        'projects',
        'projectDocumentIds',
      ])
      .pipe(switchMap(() => this.fetchProjectDocumentIds$(projectId)));
  }
}
